// ─── Sales: what the assistant may offer, and for how much ───────────────────
//
// ⚠️ THIS IS NOT A PRODUCT EDITOR. The catalogue is `products`, synced FROM
// SHOPIFY — every row here is a real thing that can be billed. This screen only
// sets the three SELLING columns: the price the assistant quotes, the words it
// uses, and the moment it is allowed to speak. (Relief 3D's price is not set
// here: it is the website's, by pack — `pack_prices`, lib/upsellPrice.ts.)
//
// ⚠️ NO PRICE MEANS NEVER OFFERED, and that is the safety. Same rule as the
// RIB: the assistant does not compose numbers. A product with an empty price is
// simply invisible to it, so a half-configured upsell cannot escape.
//
// Per-order picking lives on the order page — an upsell is chosen FOR someone,
// and that decision belongs next to their order, not in a global list.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { salesApi, settingsApi, type UpsellProductRow } from '@/lib/api';
import { useT } from '@/lib/i18n';
import { packPrices, upsellPriceLabel, upsellSellable } from '@/lib/upsellPrice';
import { SettingGroup } from './_ui';

const inputCls =
  'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white transition-colors duration-200 ' +
  'focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent';

const WHEN_LABELS: Record<string, string> = {
  preview_confirmed: 'Après validation de l’album',
  before_payment: 'Avant le paiement',
  any: 'À tout moment',
  // Express delivery's own: armed when a client names a close date
  // (worker lib/clientDeadline.ts). Missing here, the row could not be saved.
  deadline_soon: 'Client pressé (date proche)',
};

/** The express delivery row — by its catalogue handle, like the worker. */
const EXPRESS_HANDLE = 'livraison-express';

/**
 * The extras offered once, right after the client approves her 3D preview
 * (owner, 2026-10-03, item 6): the Relief 3D cover and express delivery, with
 * their prices. Her answer is added to the order, then she gets the summary
 * and the advance request (worker lib/extrasOffer.ts).
 *
 * ⚠️ `=== true` AND NOTHING ELSE, exactly as the worker reads it: a setting
 * that is missing (migration 208 not applied) draws as OFF — the switch must
 * never claim an offer the worker is not making.
 */
function ExtrasOfferCard({ products }: { products: UpsellProductRow[] }) {
  const { t } = useT();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['app-settings', 'extras_offer'], queryFn: () => settingsApi.get() });
  const on = (data?.settings as { extras_offer_enabled?: unknown } | undefined)?.extras_offer_enabled === true;
  const save = useMutation({
    mutationFn: (v: boolean) => settingsApi.update({ extras_offer_enabled: v }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['app-settings'] }),
  });

  const relief = products.find((p) => packPrices(p) != null);
  const express = products.find((p) => p.handle === EXPRESS_HANDLE);
  const reliefLive = Boolean(relief && relief.enabled && upsellSellable(relief));
  const expressLive = Boolean(express && express.enabled && Number(express.upsell_price) > 0);

  return (
    <div data-extras-offer="" className="rounded-xl border border-gray-200 bg-white px-4 py-3">
      <label className="flex items-center gap-2.5 cursor-pointer">
        <input
          type="checkbox" checked={on} disabled={save.isPending || !data}
          onChange={(e) => save.mutate(e.target.checked)}
        />
        <span className="text-[13px] font-semibold text-gray-800">{t('Proposer les options après la validation de l’album')}</span>
      </label>
      <p className="text-[11.5px] text-gray-600 mt-1.5 leading-snug">
        {t('Juste après que le client a validé son aperçu 3D, l’assistant propose une seule fois, dans un seul message : la Couverture Relief 3D (le vrai relief sur la couverture imprimée — pas l’aperçu 3D) et la livraison express, avec leurs prix. Sa réponse est ajoutée à la commande, puis il reçoit le récapitulatif et la demande d’avance.')}
      </p>
      <ul className="text-[11.5px] text-gray-700 mt-1.5 space-y-0.5">
        <li>• {t('Couverture Relief 3D')} — {relief && reliefLive ? upsellPriceLabel(relief, t) : t('pas proposée (pas de prix du site)')}</li>
        <li>• {t('Livraison express')} — {express && expressLive ? upsellPriceLabel(express, t) : t('pas proposée')}</li>
      </ul>
      {on && !expressLive && (
        <p className="text-[11.5px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-2">
          {t('Livraison express sans prix : seule la Couverture Relief 3D est proposée. Mettez un prix sur « Livraison express » ci-dessous pour la proposer aussi.')}
        </p>
      )}
    </div>
  );
}

function UpsellRow({ p }: { p: UpsellProductRow }) {
  const { t } = useT();
  const qc = useQueryClient();
  const [price, setPrice] = useState<string | null>(null);
  const [pitch, setPitch] = useState<string | null>(null);
  const [when, setWhen] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (patch: Record<string, unknown>) => salesApi.updateUpsell(p.id, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales-upsells'] });
      setPrice(null); setPitch(null); setWhen(null);
    },
  });

  const priceVal = price ?? (p.upsell_price == null ? '' : String(p.upsell_price));
  const pitchVal = pitch ?? (p.upsell_pitch ?? '');
  const whenVal = when ?? (p.upsell_offer_when || 'preview_confirmed');
  const dirty = price !== null || pitch !== null || when !== null;
  // ⚠️ RELIEF 3D IS PRICED BY PACK, LIKE ON THE WEBSITE (owner, 2026-09-29,
  // « 1a »): the assistant quotes and bills 79 / 118 / 177 for 1 / 2 / 3
  // albums — the figures the worker sends — so this screen shows them and has
  // no price field for it: a number typed here would be charged to nobody.
  const pack = packPrices(p);
  const live = upsellSellable(p);

  // ⚠️ THE SUMMARY SAYS WHETHER IT CAN BE SOLD, NOT JUST ITS PRICE. A product
  // with no price and a product not linked to Shopify are both "never offered"
  // and are fixed in completely different places — one here, one in Shopify.
  // Collapsing them into a single "inactif" would send somebody hunting in the
  // wrong screen.
  const summary = live
    // The price as the assistant quotes it — « par album » when it is priced
    // per album (express: 300 MAD on a Trio), like the chat.
    ? `${upsellPriceLabel({ ...p, upsell_price: priceVal }, t)} · ${t(WHEN_LABELS[whenVal] ?? whenVal)}`
    : !p.shopify_product_gid
      ? t('Pas relié à Shopify — ne sera jamais proposé')
      : pack
        ? t('Pas de prix du site (option coupée ?) — jamais proposé')
        : t('Pas de prix — jamais proposé');

  return (
    <SettingGroup
      title={p.title}
      summary={summary}
      // Amber for a product that cannot be sold: it looks configured from the
      // outside, and the whole point of this screen is that it is not.
      tone={live ? 'default' : 'sensitive'}
    >
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-[130px_1fr] gap-3">
        {pack ? (
          <div>
            <span className="block text-[11px] font-semibold text-gray-500 mb-1">{t('Prix (MAD)')}</span>
            <p className="px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-800 tabular-nums">
              {pack.join(' / ')}
            </p>
          </div>
        ) : (
          <div>
            <label className="block text-[11px] font-semibold text-gray-500 mb-1">{t('Prix (MAD)')}</label>
            <input
              className={inputCls} type="number" min={0} value={priceVal}
              placeholder={t('vide = jamais')}
              onChange={(e) => setPrice(e.target.value)}
            />
          </div>
        )}
        <div>
          <label className="block text-[11px] font-semibold text-gray-500 mb-1">{t('Quand le proposer')}</label>
          <select className={inputCls} value={whenVal} onChange={(e) => setWhen(e.target.value)}>
            {Object.entries(WHEN_LABELS).map(([k, lbl]) => (
              <option key={k} value={k}>{t(lbl)}</option>
            ))}
          </select>
        </div>
      </div>
      {pack && (
        <p className="text-[11.5px] text-gray-500 mt-2 leading-snug">
          {t('Solo / Duo / Trio : le prix du site, par pack — celui que l’assistant annonce et facture. Il se change dans Shopify, sur les variantes du produit.')}
        </p>
      )}

      <div className="mt-3">
        <label className="block text-[11px] font-semibold text-gray-500 mb-1">{t('Argument de vente')}</label>
        <textarea
          className={`${inputCls} min-h-[68px]`} value={pitchVal}
          placeholder={t('Ce que le client y gagne, dans vos mots. L’assistant le reformule dans sa langue.')}
          onChange={(e) => setPitch(e.target.value)}
        />
      </div>

      {dirty && (
        <div className="flex gap-2 mt-3">
          <Button
            size="sm" loading={save.isPending}
            onClick={() => save.mutate({
              // A pack-priced product has no price of its own to save.
              ...(pack ? {} : { upsell_price: priceVal === '' ? null : Number(priceVal) }),
              upsell_pitch: pitchVal.trim() || null,
              upsell_offer_when: whenVal,
            })}
          >
            {t('Enregistrer')}
          </Button>
          <Button
            size="sm" variant="secondary"
            onClick={() => { setPrice(null); setPitch(null); setWhen(null); }}
          >
            {t('Annuler')}
          </Button>
        </div>
      )}
    </div>
    </SettingGroup>
  );
}

export function SalesTab() {
  const { t } = useT();
  const [showAll, setShowAll] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['sales-upsells'],
    queryFn: () => salesApi.upsells(),
  });

  if (isLoading) return <p className="text-[12.5px] text-gray-400">{t('Chargement…')}</p>;
  if (isError) return <p className="text-[12.5px] text-red-600">{t('Impossible de charger le catalogue.')}</p>;

  const all = data?.products ?? [];
  // Upsells first — they are the point of this screen. Everything else is
  // available behind a toggle so a new add-on can be promoted without leaving.
  const upsells = all.filter((p) => p.kind === 'upsell');
  const others = all.filter((p) => p.kind !== 'upsell');
  const shown = showAll ? [...upsells, ...others] : upsells;

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
        <p className="text-[12.5px] text-gray-700 leading-snug">
          <span className="font-semibold">{t('Ces produits viennent de Shopify.')}</span>{' '}
          {t('Vous fixez ici le prix que l’assistant annonce, l’argument qu’il utilise et le moment où il a le droit d’en parler. Sans prix, il n’en parle jamais.')}
        </p>
        <p className="text-[11.5px] text-gray-600 mt-1.5 leading-snug">
          {t('Pour toute autre option, il ne propose qu’aux clients que vous avez choisis, depuis la fiche de leur commande — une seule option par message, et jamais deux fois.')}
        </p>
      </div>

      <ExtrasOfferCard products={all} />

      {shown.map((p) => <UpsellRow key={p.id} p={p} />)}

      {!shown.length && (
        <p className="text-[12.5px] text-gray-500">{t('Aucun produit marqué comme option.')}</p>
      )}

      <button
        className="text-[11.5px] font-semibold text-brand-700 underline underline-offset-2 cursor-pointer"
        onClick={() => setShowAll((v) => !v)}
      >
        {showAll
          ? t('Masquer les autres produits')
          : t('Afficher tous les produits Shopify ({n})', { n: String(others.length) })}
      </button>
    </div>
  );
}
